import React, { ReactElement, useEffect, useState } from "react";
import { Trans } from "@lingui/macro";
import { TransProps } from "@lingui/react";
import {
  SelectButtonsSmall,
  typeDataListItem,
} from "components/UI/SelectButtonsSmall/SelectButtonsSmall";
import { MainBar } from "../MainBar/MainBar";
import { Link } from "react-router-dom";
import { ROUTS } from "routs/routs";
import { typeData } from "components/UI/BarChart/BarChart";
import { useSelector } from "react-redux";
import { typeStatisticsState } from "model/dashboardStatistics/dStatisticsReducer";
import { translate } from "translate/translate";
import { ISystemState } from "model/system/systemReducer";

// Type
export type typeTabPanel = {
  variant?: string;
};

export type typeTabListItem = {
  id: string;
  checked: boolean;
  name: ReactElement<TransProps>;
};

// Constants
export const tabList: typeTabListItem[] = [
  { id: "sum-sales", checked: true, name: <Trans>Sales amount</Trans> },
  { id: "count-checks", checked: false, name: <Trans>Count of checks</Trans> },
  { id: "average-check", checked: false, name: <Trans>Average check</Trans> },
  { id: "sum-refunds", checked: false, name: <Trans>Refunds amount</Trans> },
  {
    id: "count-refunds",
    checked: false,
    name: <Trans>Count of refunds</Trans>,
  },
];

export const detailingList: typeDataListItem[] = [
  { id: "day", title: <Trans>Day</Trans> },
  { id: "week", title: <Trans>Week</Trans> },
  { id: "month", title: <Trans>Month</Trans> },
];

// Component
export const TabPanel: React.FC<typeTabPanel> = ({ variant }) => {
  let focusItem = "";
  switch (variant) {
    default:
      focusItem = "focus:bg-primary-200";
      break;
  }

  const moreLinkConstructor = (rout: string) => {
    return (
      <Link
        className="flex justify-center items-center py-1 px-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        to={rout}
      >
        <Trans>More</Trans>
      </Link>
    );
  };

  const language = useSelector(
    (state: { system: ISystemState }) => state.system.language,
  );

  const [totalLabel, setTotalLabel] = useState(
    translate("Amount of sales", language),
  );
  const [currentTabId, setCurrentTabId] = useState<string>(tabList[0].id);

  const [tabs, setTabs] = useState(tabList);

  const [idDetailing, setIdDetailing] = useState<string | null>(
    detailingList[0].id,
  );

  const [, setMoreButton] = useState(
    moreLinkConstructor(ROUTS.STATISTICS_SALES_AMOUNT_DASHBOARD),
  );

  const mainSaleDailyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainSaleAmountDayData,
  );
  const mainSaleWeeklyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainSaleAmountWeekData,
  );
  const mainSaleMonthlyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainSaleAmountMonthData,
  );

  const mainCountDailyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainSaleCountDayData,
  );
  const mainCountWeeklyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainSaleCountWeekData,
  );
  const mainCountMonthlyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainSaleCountMonthData,
  );

  const mainAverageDailyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainSaleAverageDayData,
  );
  const mainAverageWeeklyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainSaleAverageWeekData,
  );
  const mainAverageMonthlyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainSaleAverageMonthData,
  );

  const mainRefundCountDailyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainRefundCountDayData,
  );
  const mainRefundCountWeeklyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainRefundCountWeekData,
  );
  const mainRefundCountMonthlyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainRefundCountMonthData,
  );

  const mainRefundAmountDailyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainRefundAmountDayData,
  );
  const mainRefundAmountWeeklyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainRefundAmountWeekData,
  );
  const mainRefundAmountMonthlyBarData = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.mainRefundAmountMonthData,
  );

  const [mainStatisticData, setMainStatisticData] = useState<typeData | null>(
    null,
  );

  const setMainData = (tabId: string) => {
    //console.log('TabPanel', idDetailing )
    switch (tabId) {
      // Checks sale count
      case tabList[1].id:
        switch (idDetailing) {
          case detailingList[1].id:
            setMainStatisticData(mainCountWeeklyBarData);
            break;
          case detailingList[2].id:
            setMainStatisticData(mainCountMonthlyBarData);
            break;
          default:
            setMainStatisticData(mainCountDailyBarData);
        }
        break;

      // Checks sale average
      case tabList[2].id:
        switch (idDetailing) {
          case detailingList[1].id:
            setMainStatisticData(mainAverageWeeklyBarData);
            break;
          case detailingList[2].id:
            setMainStatisticData(mainAverageMonthlyBarData);
            break;
          default:
            setMainStatisticData(mainAverageDailyBarData);
        }
        break;

      // Checks refund count
      case tabList[4].id:
        switch (idDetailing) {
          case detailingList[1].id:
            setMainStatisticData(mainRefundCountWeeklyBarData);
            break;
          case detailingList[2].id:
            setMainStatisticData(mainRefundCountMonthlyBarData);
            break;
          default:
            setMainStatisticData(mainRefundCountDailyBarData);
        }
        break;

      // Checks refund amount
      case tabList[3].id:
        switch (idDetailing) {
          case detailingList[1].id:
            setMainStatisticData(mainRefundAmountWeeklyBarData);
            break;
          case detailingList[2].id:
            setMainStatisticData(mainRefundAmountMonthlyBarData);
            break;
          default:
            setMainStatisticData(mainRefundAmountDailyBarData);
        }
        break;

      // Sales sale amount
      default:
        switch (idDetailing) {
          case detailingList[1].id:
            setMainStatisticData(mainSaleWeeklyBarData);
            break;
          case detailingList[2].id:
            setMainStatisticData(mainSaleMonthlyBarData);
            break;
          default:
            setMainStatisticData(mainSaleDailyBarData);
        }
    }
  };

  useEffect(() => {
    setMainData(currentTabId);
    // eslint-disable-next-line
  }, [
    idDetailing,
    currentTabId,
    mainSaleDailyBarData,
    mainSaleWeeklyBarData,
    mainSaleMonthlyBarData,
    mainCountDailyBarData,
    mainCountWeeklyBarData,
    mainCountMonthlyBarData,
    mainAverageDailyBarData,
    mainAverageWeeklyBarData,
    mainAverageMonthlyBarData,
    mainRefundCountDailyBarData,
    mainRefundCountWeeklyBarData,
    mainRefundCountMonthlyBarData,
    mainRefundAmountDailyBarData,
    mainRefundAmountWeeklyBarData,
    mainRefundAmountMonthlyBarData,
  ]);

  const onClick = (id: string) => {
    const newTabs = tabs.map((item) => {
      return {
        ...item,
        ...{
          checked: id === item.id,
        },
      };
    });
    setTabs(newTabs);
    setCurrentTabId(id);

    switch (id) {
      case "count-checks":
        setMoreButton(
          moreLinkConstructor(ROUTS.STATISTICS_CHECKS_COUNT_DASHBOARD),
        );
        setTotalLabel(translate("Count of checks", language));
        break;
      case "average-check":
        setMoreButton(
          moreLinkConstructor(ROUTS.STATISTICS_AVERAGE_CHECK_DASHBOARD),
        );
        setTotalLabel(translate("Average check", language));
        break;
      case "count-refunds":
        setMoreButton(
          moreLinkConstructor(ROUTS.STATISTICS_REFUNDS_COUNT_DASHBOARD),
        );
        setTotalLabel(translate("Count of refunds", language));
        break;
      case "sum-refunds":
        setMoreButton(
          moreLinkConstructor(ROUTS.STATISTICS_REFUNDS_AMOUNT_DASHBOARD),
        );
        setTotalLabel(translate("Amount of refunds", language));
        break;
      default:
        setMoreButton(
          moreLinkConstructor(ROUTS.STATISTICS_SALES_AMOUNT_DASHBOARD),
        );
        setTotalLabel(translate("Amount of sales", language));
    }
  };

  return (
    <div className="relative flex flex-col gap-4 border overflow-x-auto rounded-md">
      <div className="p-3 flex flex-col gap-4">
        <div className="flex justify-between gap-4">
          <SelectButtonsSmall
            dataList={detailingList}
            checkedId={idDetailing}
            setCheckedId={setIdDetailing}
          />
          {/*
                        {moreButton}
                    */}
        </div>
        <div className="p-4 w-full h-96 flex flex-col justify-center">
          {/* @ts-ignore */}
          <MainBar
            amountTitle={totalLabel}
            mainStatisticData={mainStatisticData}
          />
        </div>
      </div>
      <div className="flex">
        {tabs.map((item, index) => {
          return (
            <button
              key={index}
              className={`flex-grow flex flex-col items-center justify-items-center p-4 focus:outline-none 
                            ${item.checked ? "" : " bg-gray-100 border-t text-gray-600 " + focusItem}
                            ${tabs.length - 1 === index ? "" : " border-r"}`}
              onClick={() => onClick(item.id)}
            >
              {item.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};
